/**
 * LibraryCollectionsIndex — the typographic collections index for the library
 * landing (library-surface redesign, C1 pick — .plan/features/2026-07-03-
 * library-surface-redesign.md §C1, comp .plan/comps/library-comps.html).
 *
 * Replaces the old filled-accent pill row (LibraryCollectionChips) with a quiet
 * text index, the way a document lists its sections:
 *
 *   LIBRARY                              ← 11px mono uppercase slab
 *   Everything · Breads 12 · Pastry 4 · + New
 *   ───────────────────────────────────  ← hairline rule
 *
 * Register (design.md §2):
 *   - Collections are inline TEXT links (Inter 13, muted), count in mono
 *     tabular-nums after the name. No pills, no filled shapes, no 999px radii,
 *     no embedded kebab menus — the pill idiom over-spent the accent and read as
 *     gadget chrome (redesign §C1 diagnosis).
 *   - Selected entry = var(--text), weight 500, with a 2px accent underline —
 *     the sub-mode-active-underline idiom, the ONLY accent spend on this surface
 *     (a hairline, not a fill). Multi-select preserved: several entries can be
 *     underlined at once; the faceting union lives in LibraryGridView.
 *   - "Everything" leads the line (the "clear all" affordance). "+ New" is a
 *     muted text affordance at the end.
 *
 * Manage (rename / delete) moves off the chip entirely: when EXACTLY ONE
 * collection is selected, a muted `Rename · Delete` text pair appears at the
 * line's right end (reusing CollectionRenameModal / CollectionDeleteModal).
 * Right-click / long-press on a collection selects it (revealing that inline
 * pair) — the preserved context-menu shortcut, now without a floating kebab.
 *
 * §2a discipline:
 *   - The index ALWAYS renders (even with zero collections) so the
 *     `Everything · + New` affordance stays discoverable (design.md §6.0a).
 *   - Counts render only when known (honest UI — no fabricated 0).
 *
 * Design refs:
 *   design.md §6.3 — library organization surface.
 *   design.md §2 — accent reserved for signal; the active underline is the one
 *     allowed accent use here; hairlines over shadows; one accent only.
 *   design.md §6.0a — the create affordance stays discoverable.
 *   design.md §13a.1 — collection rename / delete modals.
 *   design.md §4 — tokens.
 *   branding.md §5.2.1 / §8 — "collection" vocabulary; no banned words.
 *
 * CLAUDE.md Rule 7 — the line wraps at 375px (no horizontal scroll); 8px
 *   vertical padding per link gives wrapped rows a ~44px tap target.
 * CLAUDE.md Rule 8 — the rename / delete / create modals surface their own
 *   failures inline.
 */

import { Fragment, useState } from "react";
import type { Collection } from "~/features/explorer/types";
import { CollectionCreateModal } from "~/features/explorer/CollectionCreateModal";
import { CollectionRenameModal } from "~/features/explorer/CollectionRenameModal";
import { CollectionDeleteModal } from "~/features/explorer/CollectionDeleteModal";

// ─── CSS injection ────────────────────────────────────────────────────────────

const INDEX_CSS = `
.proof-collections-index {
  padding: 12px 16px 0;
}

.proof-index {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 7px;
  /* Wrapped-row tap-zone separation (Rule 7, design.md §4.3). Each link's
     coarse-pointer ::after hit overlay is a 44px box centred on the ~34px visual
     box, so it overhangs ~5px past each edge. With row-gap:0 adjacent wrapped
     rows' overlays overlapped (~10px). --space-3 (12px) clears the combined
     overhang so the 44px floors stay intact without colliding. */
  row-gap: var(--space-3, 12px);
  margin-top: 10px;
  font-family: var(--font-sans, 'Inter', system-ui, sans-serif);
  font-size: 13px;
  color: var(--muted, #71685b);
}

/* Each entry gets 8px vertical padding so wrapped rows reach a ~44px tap target
   at 375px (Rule 7). line-height 1.4 carries the rest. */
.proof-index__link {
  display: inline-flex;
  align-items: baseline;
  color: var(--muted, #71685b);
  background: none;
  border: none;
  font: inherit;
  text-decoration: none;
  cursor: pointer;
  padding: 8px 1px;
  line-height: 1.4;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
  transition: color 200ms ease;
  position: relative;
}

/* §4.3 tap floor: the visual box is ~34px (comp-exact); extend the HIT AREA to
   44px on touch with a transparent overlay so the wrapped line's rhythm never
   changes (same idiom as .proof-recipe-header__collection-edit--icon). */
@media (pointer: coarse) {
  .proof-index__link::after,
  .proof-index__manage-btn::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    min-height: var(--tap-target-min, 44px);
    height: 100%;
  }
}

.proof-index__link:hover {
  color: var(--text, #1a1714);
}

/* Selected — var(--text), weight 500, 2px accent underline (the ONLY accent
   spend on this surface: the sub-mode-active-underline idiom, design.md §2). */
.proof-index__link--sel {
  color: var(--text, #1a1714);
  font-weight: 500;
  padding-bottom: 6px;
  border-bottom: 2px solid var(--accent, #c23d12);
}

.proof-index__count {
  font-family: var(--font-mono, 'Geist Mono', monospace);
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  color: var(--faint, #766d60);
  margin-left: 4px;
}

.proof-index__sep {
  color: var(--faint, #766d60);
}

.proof-index__new {
  color: var(--faint, #766d60);
}

/* Rename · Delete pair — pushed to the line's right end; muted text buttons,
   no fill, no accent. Appears only when exactly one collection is selected. */
.proof-index__manage {
  margin-left: auto;
  display: inline-flex;
  align-items: baseline;
  column-gap: 7px;
  color: var(--faint, #766d60);
}

.proof-index__manage-btn {
  color: var(--muted, #71685b);
  background: none;
  border: none;
  font: inherit;
  cursor: pointer;
  padding: 8px 1px;
  line-height: 1.4;
  -webkit-tap-highlight-color: transparent;
  transition: color 200ms ease;
  position: relative;
}

.proof-index__manage-btn:hover {
  color: var(--text, #1a1714);
}

.proof-index__link:focus-visible,
.proof-index__manage-btn:focus-visible {
  outline: 2px solid var(--accent, #c23d12);
  outline-offset: 2px;
  border-radius: 2px;
}

.proof-index-rule {
  border-bottom: 1px solid var(--line, #dcd7cc);
  margin-top: 6px;
}
`;

if (typeof document !== "undefined") {
  const id = "proof-collections-index-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = INDEX_CSS;
    document.head.appendChild(el);
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface LibraryCollectionsIndexProps {
  /** All of the user's collections. May be empty — the index still renders (§2a). */
  collections: Collection[];
  /**
   * Currently selected collection UUIDs (multi-select, OR-within). Empty =
   * "Everything" selected (no collection filter).
   */
  activeCollectionUuids: string[];
  /**
   * Called when the user toggles a collection entry. Empty string "" signals
   * "clear all" from the "Everything" entry.
   */
  onToggleCollection: (uuid: string) => void;
  /**
   * Recipe count per collection UUID, for the mono count after each name.
   * Optional — when a UUID is absent the count is omitted (honest UI; no 0).
   */
  counts?: Record<string, number>;
  /**
   * Called with the new collection name when the user creates one via "+ New" →
   * CollectionCreateModal. Resolves on success / rejects on error (the modal
   * surfaces the inline error). When absent "+ New" is not rendered.
   */
  onCreateCollection?: (name: string) => Promise<void>;
  /**
   * Called when the user renames the single-selected collection via the inline
   * "Rename" affordance → CollectionRenameModal. Resolves on success / rejects
   * on error (the modal surfaces the inline error). When absent "Rename" is not
   * rendered (honest UI).
   */
  onRenameCollection?: (uuid: string, newName: string) => Promise<void>;
  /**
   * Called when the user deletes the single-selected collection via the inline
   * "Delete" affordance → CollectionDeleteModal. Resolves on success / rejects
   * on error (the modal surfaces the inline error). When absent "Delete" is not
   * rendered.
   */
  onDeleteCollection?: (uuid: string) => Promise<void>;
}

export function LibraryCollectionsIndex({
  collections,
  activeCollectionUuids,
  onToggleCollection,
  counts,
  onCreateCollection,
  onRenameCollection,
  onDeleteCollection,
}: LibraryCollectionsIndexProps) {
  const [showCreate, setShowCreate] = useState(false);
  const [renameTarget, setRenameTarget] = useState<Collection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Collection | null>(null);

  const everythingSelected = activeCollectionUuids.length === 0;

  // The inline Rename · Delete pair appears ONLY when exactly one collection is
  // selected (redesign §C1). Resolve that lone collection so the modals target it.
  const soleSelected =
    activeCollectionUuids.length === 1
      ? (collections.find((c) => c.uuid === activeCollectionUuids[0]) ?? null)
      : null;
  const canRename = onRenameCollection !== undefined;
  const canDelete = onDeleteCollection !== undefined;
  const showManage = soleSelected !== null && (canRename || canDelete);

  function handleEverythingClick() {
    if (activeCollectionUuids.length > 0) onToggleCollection("");
  }

  return (
    <>
      <div className="proof-collections-index" data-testid="collections-index">
        {/* The redundant mono "Library" slab was removed 2026-07-11: on the
            dedicated /app/library page, the LibraryView H1 is the only "Library"
            label. The collection filter below is the surface's sole content here. */}
        <div className="proof-index" role="group" aria-label="Filter by collection">
          <button
            type="button"
            className={`proof-index__link${everythingSelected ? " proof-index__link--sel" : ""}`}
            data-testid="collection-index-everything"
            onClick={handleEverythingClick}
            aria-pressed={everythingSelected}
          >
            Everything
          </button>

          {collections.map((collection) => {
            const isActive = activeCollectionUuids.includes(collection.uuid);
            const count = counts?.[collection.uuid];
            return (
              <Fragment key={collection.uuid}>
                <span className="proof-index__sep" aria-hidden="true">
                  ·
                </span>
                <button
                  type="button"
                  className={`proof-index__link${isActive ? " proof-index__link--sel" : ""}`}
                  data-testid={`collection-index-${collection.uuid}`}
                  onClick={() => onToggleCollection(collection.uuid)}
                  // Right-click / long-press selects this collection — the
                  // preserved context-menu shortcut that reveals the inline
                  // Rename · Delete pair, with no floating kebab menu.
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (!isActive) onToggleCollection(collection.uuid);
                  }}
                  aria-pressed={isActive}
                >
                  {collection.name}
                  {typeof count === "number" && <span className="proof-index__count">{count}</span>}
                </button>
              </Fragment>
            );
          })}

          {onCreateCollection !== undefined && (
            <>
              <span className="proof-index__sep" aria-hidden="true">
                ·
              </span>
              <button
                type="button"
                className="proof-index__link proof-index__new"
                data-testid="collection-index-add"
                aria-label="Add a collection"
                onClick={() => setShowCreate(true)}
              >
                + New
              </button>
            </>
          )}

          {showManage && soleSelected !== null && (
            <span className="proof-index__manage" data-testid="collection-index-manage">
              {canRename && (
                <button
                  type="button"
                  className="proof-index__manage-btn"
                  data-testid="collection-index-rename"
                  onClick={() => setRenameTarget(soleSelected)}
                >
                  Rename
                </button>
              )}
              {canRename && canDelete && (
                <span className="proof-index__sep" aria-hidden="true">
                  ·
                </span>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="proof-index__manage-btn"
                  data-testid="collection-index-delete"
                  onClick={() => setDeleteTarget(soleSelected)}
                >
                  Delete
                </button>
              )}
            </span>
          )}
        </div>
        <div className="proof-index-rule" aria-hidden="true" />
      </div>

      {showCreate && onCreateCollection !== undefined && (
        <CollectionCreateModal onCreate={onCreateCollection} onClose={() => setShowCreate(false)} />
      )}

      {renameTarget !== null && onRenameCollection !== undefined && (
        <CollectionRenameModal
          collectionUuid={renameTarget.uuid}
          currentName={renameTarget.name}
          onRename={onRenameCollection}
          onClose={() => setRenameTarget(null)}
        />
      )}

      {deleteTarget !== null && onDeleteCollection !== undefined && (
        <CollectionDeleteModal
          collectionUuid={deleteTarget.uuid}
          collectionName={deleteTarget.name}
          onDelete={onDeleteCollection}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}
